"use client";

import { useEffect, useState, ReactElement } from "react";
import type { PropsWithChildren } from "react";

type Trigger = "scroll" | "touch" | "click" | string;

interface LazyLoadAfterTriggerWaitProps {
  lazy: "eager" | "lazy";
  triggers: Trigger[];
  autoTrigger?: boolean;
  selector?: string;
  dontLoadUntilEager?: boolean;
  ComponentLazyLoad?: ReactElement;
  triggerCallback?: (event: any, triggerShow: () => void) => void;
}

function LazyLoadAfterTriggerWait({
  lazy,
  children,
  selector,
  triggers,
  autoTrigger,
  dontLoadUntilEager = false,
  ComponentLazyLoad,
  triggerCallback,
}: PropsWithChildren<LazyLoadAfterTriggerWaitProps>) {
  const [show, setShow] = useState<boolean>(!dontLoadUntilEager && lazy === "eager");

  useEffect(() => {
    if (!show && dontLoadUntilEager) {
      if (dontLoadUntilEager && lazy === "eager") {
        setShow(true);
      }
      return;
    }

    if (dontLoadUntilEager || show) {
      return;
    }

    //in case of props failure show component
    if (!triggers || (triggers ?? []).length === 0) {
      setShow(true);
      return;
    }

    //in case of props failure show component
    if (!autoTrigger && typeof triggerCallback !== "function") {
      setShow(true);
      return;
    }

    //unmount listeners to preserve performance
    function unmount() {
      const mount = document.querySelector(selector ?? "empty") ?? window;
      for (const trigger of triggers) {
        mount.removeEventListener(trigger, handleListener);
      }
    }

    //handle a selector/trigger listener
    const handleListener = (e: any) => {
      if (autoTrigger) {
        setShow(true);
        unmount();
      }

      if (typeof triggerCallback === "function") {
        triggerCallback(e, () => {
          setShow(true);
          unmount();
        });
      }
    };

    //mount triggers based on selector
    const mount = document.querySelector(selector ?? "empty") ?? window;
    for (const trigger of triggers) {
      mount.addEventListener(trigger, handleListener, { passive: true });
    }

    return () => {
      try {
        unmount();
      } catch (error) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggers, autoTrigger, show, lazy, selector, dontLoadUntilEager]);

  return show ? <>{children}</> : <>{ComponentLazyLoad}</>;
}

export default LazyLoadAfterTriggerWait;
